import moment from 'moment';
import commonStore from '../../../../store/common';
import { isEsmaTableDisabledForFund } from './visibleSections';

function isValidDate(d) {
  return d instanceof Date && !isNaN(d);
}

const PARAGRAPH_LINE_HEIGHT = 17;
const DEFAULT_ROW_HEIGHT = 32;
const WARNING_EXTRA_HEIGHT = 20;

// Cap to bound memory if many distinct (width, text) pairs appear; in
// practice the same error/warning text and the same column widths repeat
// across funds and re-renders, so the cache saturates quickly and stays warm.
const MEASUREMENT_CACHE_LIMIT = 10000;

// Cached paragraph height — measuring used to create + append + measure +
// remove a <p> per call (two layout reflows). We now create the element once
// and reuse it, and cache results by `width|text` — typical reports re-measure
// the same content many times across funds, columns, and re-renders.
const __paragraphHeightCache = new Map();
let __paragraphHeightEl = null;

export function getTextHeightForParagraph(text, width) {
  const key = `${width}|${text}`;
  const cached = __paragraphHeightCache.get(key);
  if (cached !== undefined) return cached;

  if (!__paragraphHeightEl) {
    __paragraphHeightEl = document.createElement('p');
    __paragraphHeightEl.style.cssText =
      'position:fixed;top:0;left:0;visibility:hidden;font-size:12px;line-height:17px;' +
      'word-break:break-word;overflow-wrap:break-word;padding:6px 8px;' +
      'white-space:normal;box-sizing:border-box';
    document.body.appendChild(__paragraphHeightEl);
  }
  __paragraphHeightEl.style.width = `${width - 16}px`;
  __paragraphHeightEl.innerHTML = text;

  const measured = __paragraphHeightEl.getBoundingClientRect().height;
  const result = measured === 0 ? 40 : measured + 18;

  if (__paragraphHeightCache.size >= MEASUREMENT_CACHE_LIMIT) __paragraphHeightCache.clear();
  __paragraphHeightCache.set(key, result);
  return result;
}

// Same approach as the paragraph cache above — one reused hidden element and a
// (width, text) → lines cache. The same text wraps to the same line count at
// the same width, so the first measurement is reused for every later call.
const __segmentLineCache = new Map();
let __segmentLineEl = null;

// Measures how many lines a single plain-text segment wraps into in `width`px.
function measureSegmentLines(text, width) {
  const key = `${width}|${text}`;
  const cached = __segmentLineCache.get(key);
  if (cached !== undefined) return cached;

  if (!__segmentLineEl) {
    __segmentLineEl = document.createElement('p');
    __segmentLineEl.style.cssText =
      'position:fixed;top:0;left:0;visibility:hidden;font-size:12px;line-height:' +
      `${PARAGRAPH_LINE_HEIGHT}px;word-break:break-word;overflow-wrap:break-word;` +
      'white-space:normal;box-sizing:border-box';
    document.body.appendChild(__segmentLineEl);
  }
  __segmentLineEl.style.width = `${width - 16}px`;
  __segmentLineEl.textContent = text;

  const lines = Math.max(
    1,
    Math.round(__segmentLineEl.getBoundingClientRect().height / PARAGRAPH_LINE_HEIGHT)
  );

  if (__segmentLineCache.size >= MEASUREMENT_CACHE_LIMIT) __segmentLineCache.clear();
  __segmentLineCache.set(key, lines);
  return lines;
}

// Counts how many lines `text` occupies when rendered in a cell of `width`px.
// `<br/>` markers (e.g. a stacked error + warning message) are treated as
// explicit line breaks. Returns at least 1 — an empty cell still takes a line.
export function getLineCount(text, width) {
  if (!text) return 1;

  return String(text)
    .split(/<br\s*\/?>/i)
    .reduce((total, segment) => total + measureSegmentLines(segment, width), 0);
}

// Computes a table row height from its content. A single line of text renders
// at 32px; each extra wrapped line adds one line-height. The label sits in its
// own column; every value column stacks its error/warning message under its
// own value, so each cell needs value-lines + error-lines. The row fits the
// tallest cell (or the label, whichever is taller).
//
// Pass either a single `value`/`error` pair, or `cells` — an array of
// `{ value, error }` pairs, one per value column. `cells` is required for
// multi-column rows: taking `max(valueLines)` and `max(errorLines)` separately
// across columns pairs a long value with a different column's long error and
// over-sizes the row.
export function getRowHeight({
  label,
  labelWidth,
  value,
  error,
  cells,
  valueWidth,
  hasWarning = false
}) {
  const labelLines = getLineCount(label, labelWidth);

  const valueCells = cells ?? [{ value, error }];
  const cellLines = valueCells.map(
    cell =>
      getLineCount(cell.value, valueWidth) + (cell.error ? getLineCount(cell.error, valueWidth) : 0)
  );

  const rowLines = Math.max(labelLines, ...cellLines);

  return (
    DEFAULT_ROW_HEIGHT +
    (rowLines - 1) * PARAGRAPH_LINE_HEIGHT +
    (hasWarning ? WARNING_EXTRA_HEIGHT : 0)
  );
}

// Resolves the rendered label/value column widths for a report table, matching
// the layout the `columns` memo builds. The hidden line-count measurement must
// use the same width the cell is actually rendered at, otherwise text wraps a
// different number of times in the measurement than on screen.
export function getCellWidths({ isMultiFunds, isComparisonReport, fundSize, contentWidth }) {
  if (!isMultiFunds) {
    const width = contentWidth / (isComparisonReport ? 3 : 2);
    return { labelWidth: width, valueWidth: width };
  }

  if (fundSize <= 3 && !isComparisonReport) {
    const width = contentWidth / (fundSize + 1);
    return { labelWidth: width, valueWidth: width };
  }

  return { labelWidth: 200, valueWidth: 300 };
}

export function getBiggestString(strings) {
  if (!strings || strings?.length === 0) return ''; // Return empty if array is empty

  return strings.reduce(
    (longest, current) => (current.length > longest.length ? current : longest),
    strings[0]
  );
}

// Resolves a stored cell value to the text the user actually sees: for
// option-backed fields (select) the matching option label, otherwise the value
// itself. Used so row-height measurements match the rendered cell — measuring
// the raw option code instead of its (often much longer) label clips the row.
export function getDisplayValue(value, options) {
  if (options) {
    return value ? String(options.find(option => option.value === value)?.label ?? '') : '';
  }
  return value != null ? String(value) : '';
}

// Joins a cell's error and warning messages into the single string it renders,
// stacking them with an explicit line break when both are present.
export function combineMessage(errorMsg, warningMsg) {
  const error = errorMsg || '';
  const warning = warningMsg || '';
  if (error && warning) return `${error}<br/>${warning}`;
  return error || warning;
}

// Builds getRowHeight's `cells` — one { value, error } pair per value column.
// `rows` holds the data object behind each column (one per fund, or a single
// element for single-fund tables). Pairing each column's own value with its
// own error lets getRowHeight size the row to the tallest cell instead of
// summing the longest value with an unrelated column's longest error.
// `includeWarning` is false for tables that account for warnings separately.
export function getRowCells(rows, key, options, includeWarning = true) {
  return rows.map(row => ({
    value: getDisplayValue(row?.[key], options),
    error: includeWarning
      ? combineMessage(row?.errors?.[key]?.msg, row?.warning?.[key]?.msg)
      : row?.errors?.[key]?.msg || ''
  }));
}

export function getColumnByType({
  value,
  key,
  type,
  options,
  defaultValue,
  index,
  required,
  ...restProps
}) {
  const isSectionDisabled =
    restProps.isSectionDisabled ?? isEsmaTableDisabledForFund(value, restProps.tableName);
  const sharedProps = { ...restProps, isSectionDisabled };

  if (isSectionDisabled) {
    return {
      ...sharedProps,
      type: 'customText',
      text: 'N/A',
      dataKey: key,
      dataIndex: index,
      nonEditable: true,
      required: false,
      errorMessage: null
    };
  }

  switch (type) {
    case 'select': {
      return {
        type: type,
        options,
        selectedValue: value[key] === undefined ? '' : value[key],
        inputValue: value[key] === undefined ? '' : value[key],
        dataKey: key,
        dataIndex: index,
        isStaticField: value.static_template_fields
          ? value.static_template_fields.includes(key)
          : false,
        validator: () => !(value?.errors && value?.errors[key]),
        required: !commonStore.isViewer && required ? value[key] === defaultValue : false,
        errorMessage:
          !commonStore.isViewer && value?.errors && value?.errors[key] && value?.errors[key].msg
            ? value?.errors[key].msg
            : null,
        ...sharedProps
      };
    }
    case 'customText': {
      return {
        type: type,
        text: value[key],
        isStaticField: value.static_template_fields
          ? value.static_template_fields.includes(key)
          : false,
        dataKey: key,
        dataIndex: index,
        nonEditable: key === 'ranking',
        style:
          key === 'ranking'
            ? {
                background: '#EDF0F5'
              }
            : {},
        validator: () => !(value?.errors && value?.errors[key]),
        required: !commonStore.isViewer && required ? value[key] === defaultValue : false,
        errorMessage:
          !commonStore.isViewer && value?.errors && value?.errors[key] && value?.errors[key].msg
            ? value?.errors[key].msg
            : null,
        ...sharedProps
      };
    }
    case 'customNumber': {
      return {
        type: type,
        value: value[key],
        dataKey: key,
        dataIndex: index,
        isStaticField: value.static_template_fields
          ? value.static_template_fields.includes(key)
          : false,
        validator: () => !(value?.errors && value?.errors[key]),
        required: !commonStore.isViewer && required ? value[key] === defaultValue : false,
        errorMessage:
          !commonStore.isViewer && value?.errors && value?.errors[key] && value?.errors[key].msg
            ? value?.errors[key].msg
            : null,
        ...sharedProps
      };
    }
    case 'customBoolean': {
      return {
        type: type,
        isStaticField: value.static_template_fields
          ? value.static_template_fields.includes(key)
          : false,
        options,
        selectedValue: value[key],
        inputValue: value[key],
        dataKey: key,
        dataIndex: index,
        validator: () => !(value?.errors && value?.errors[key]),
        required: !commonStore.isViewer && required ? value[key] === defaultValue : false,
        errorMessage:
          !commonStore.isViewer && value?.errors && value?.errors[key] && value?.errors[key].msg
            ? value?.errors[key].msg
            : null,
        ...sharedProps
      };
    }
    case 'customDate': {
      return {
        type: type,
        date: value[key] !== null ? new Date(value[key]) : null,
        isStaticField: value.static_template_fields
          ? value.static_template_fields.includes(key)
          : false,
        dataKey: key,
        dataIndex: index,
        validator: () => !(value?.errors && value?.errors[key]),
        required: !commonStore.isViewer && required ? value[key] === defaultValue : false,
        errorMessage:
          !commonStore.isViewer && value?.errors && value?.errors[key] && value?.errors[key].msg
            ? value?.errors[key].msg
            : null,
        ...sharedProps
      };
    }

    case 'customDateTime': {
      return {
        type: type,
        date: value[key] !== null ? new Date(value[key]) : null,
        isStaticField: value.static_template_fields
          ? value.static_template_fields.includes(key)
          : false,
        dataKey: key,
        dataIndex: index,
        validator: () => !(value?.errors && value?.errors[key]),
        required: !commonStore.isViewer && required ? value[key] === defaultValue : false,
        errorMessage:
          !commonStore.isViewer && value?.errors && value?.errors[key] && value?.errors[key].msg
            ? value?.errors[key].msg
            : null,
        ...sharedProps
      };
    }
  }

  return {
    ...sharedProps,
    type: 'customText',
    text: getDisplayValue(value?.[key], options),
    dataKey: key,
    dataIndex: index,
    isStaticField: value?.static_template_fields
      ? value.static_template_fields.includes(key)
      : false,
    nonEditable: true,
    required: false,
    errorMessage:
      !commonStore.isViewer && value?.errors && value?.errors[key] && value?.errors[key].msg
        ? value?.errors[key].msg
        : null
  };
}

export function updateDataByType({ cell }) {
  switch (cell.type) {
    case 'select': {
      return cell.selectedValue;
    }
    case 'checkbox': {
      return cell.checked;
    }
    case 'customText': {
      return cell.text;
    }
    case 'customNumber': {
      return cell.value;
    }
    case 'customBoolean': {
      return cell.selectedValue === 'Yes' ? true : cell.selectedValue === 'No' ? false : null;
    }
    case 'customDate': {
      return isValidDate(cell.date) ? moment.utc(cell.date).format('YYYY-MM-DD') : null;
    }

    case 'customDateTime': {
      return isValidDate(cell.date) ? cell.date : null;
    }
  }
}

export const getDefaultValueByType = value => {
  if (value.type.includes('string') && value.format === 'date') {
    return null;
  }

  if (value.type.includes('string')) {
    return '';
  }
  if (value.type.includes('integer') || value.type.includes('number')) {
    return null;
  }

  if (value.type.includes('boolean')) {
    return null;
  }
  if (value.type.includes('array')) {
    return '';
  }
};

export const getTypeFromSchema = value => {
  if (value.type.includes('array')) {
    return 'select';
  }

  if (value.type.includes('string') && value.format === 'date') {
    return 'customDate';
  }

  if (value.type.includes('date')) {
    return 'customDate';
  }

  if (value.type.includes('string') && value.format === 'date-time') {
    return 'customDateTime';
  }

  if (value.type.includes('string')) {
    return 'customText';
  }
  if (value.type.includes('integer') || value.type.includes('number')) {
    return 'customNumber';
  }

  if (value.type.includes('boolean')) {
    return 'customBoolean';
  }
};

export const getKeyByType = type => {
  if (type === 'select') {
    return 'selectedValue';
  }

  if (type === 'customText') {
    return 'text';
  }
  if (type === 'customNumber') {
    return 'value';
  }

  if (type === 'customBoolean') {
    return 'selectedValue';
  }

  if (type === 'customDate') {
    return 'date';
  }
  if (type === 'customDateTime') {
    return 'date';
  }
};

export const filterArrayByFieldsWithStop = (array, fields) => {
  const result = [];

  for (const obj of array) {
    if (
      fields
        .filter(field => field !== 'ranking')
        .some(
          field =>
            obj[field] !== '' &&
            obj[field] !== null &&
            obj[field] !== undefined &&
            !Number.isNaN(obj[field])
        )
    ) {
      result.push(obj);
    } else if (obj.id && fields.includes('ranking')) {
      result.push({
        ...obj,
        is_deleted: true
      });
    } else if (obj.id) {
      result.push(obj);
    }
  }

  return result;
};

export const filterArrayByFieldsNotEmptyValue = (array, fields) => {
  const result = [];

  for (const obj of array) {
    if (
      fields
        .filter(field => field !== 'ranking')
        .some(
          field =>
            obj[field] !== '' &&
            obj[field] !== null &&
            obj[field] !== undefined &&
            !Number.isNaN(obj[field])
        )
    ) {
      result.push(obj);
    } else if (obj.id) {
      result.push({
        ...obj,
        is_deleted: true
      });
    }
  }

  return result;
};

/**
 * Validates a field value against the schema rules returned by the backend.
 * Returns an error message string when validation fails,
 * null when validation passes (safe to clear existing error),
 * or undefined when no applicable rules exist (leave existing errors untouched).
 */
export function validateFieldRules(value, rules) {
  if (!rules) return undefined;

  const strRules = rules.string || {};
  const numRules = rules.number || rules.integer || {};
  const hasRules = Object.keys(strRules).length > 0 || Object.keys(numRules).length > 0;

  if (!hasRules) return undefined;
  if (value === null || value === undefined || value === '') return null;

  if (typeof value === 'string') {
    if (strRules.maxLength !== undefined && value.length > strRules.maxLength)
      return `Must be at most ${strRules.maxLength} characters`;
    if (strRules.minLength !== undefined && value.length < strRules.minLength)
      return `Must be at least ${strRules.minLength} characters`;
    if (strRules.pattern && !new RegExp(strRules.pattern).test(value)) return `Invalid format`;
  }

  if (typeof value === 'number') {
    if (numRules.maximum !== undefined && value > numRules.maximum)
      return `Must be at most ${numRules.maximum}`;
    if (numRules.minimum !== undefined && value < numRules.minimum)
      return `Must be at least ${numRules.minimum}`;
  }

  return null;
}

export const filterArrayByFieldsForMultipleTable = (array, fields) => {
  const result = [];

  for (const obj of array) {
    if (
      fields.some(
        field =>
          obj[field] !== '' &&
          obj[field] !== null &&
          obj[field] !== undefined &&
          !Number.isNaN(obj[field])
      )
    ) {
      result.push(obj);
    } else if (obj.id) {
      result.push({
        ...obj,
        is_deleted: true
      });
    } else {
      result.push(obj);
    }
  }

  return result;
};
